import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse, HttpClient, HttpBackend } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AdminAuthService } from './admin-auth.service';
import { NotificationService } from '../../core/services/notification.service';
import { AdminTokenResponse } from './admin.models';
import { environment } from '../../../environments/environment';

// Shared across concurrent requests. While a refresh is in flight, other 401s WAIT on
// `refreshedToken$` for the new token and retry — instead of forcing a logout (which caused
// Issue 10: parallel /admin/tenants + /admin/plans 401s logged the admin out mid-refresh).
let isRefreshing = false;
const refreshedToken$ = new BehaviorSubject<string | null>(null);

/**
 * Interceptor for the admin portal. Only acts on /api/admin/* requests; everything
 * else passes through untouched so the tenant auth interceptor handles it.
 */
export const adminInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  if (!req.url.includes('/api/admin/')) return next(req);

  const auth = inject(AdminAuthService);
  const notify = inject(NotificationService);

  // The login/refresh/logout endpoints are anonymous — don't attach a token.
  const isAuthEndpoint = req.url.includes('/api/admin/auth/');
  const authed = isAuthEndpoint ? req : attachToken(req, auth.getToken());

  return next(authed).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 403) {
        notify.error('You don’t have permission to do that.');
        return throwError(() => err);
      }

      if (err.status !== 401 || isAuthEndpoint) return throwError(() => err);

      const refreshToken = auth.getRefreshToken();
      if (!refreshToken) {
        auth.logout();
        return throwError(() => err);
      }

      // A refresh is already running — wait for it to finish, then retry with the new token
      // (do NOT log out here; the concurrent refresh is likely succeeding).
      if (isRefreshing) {
        return refreshedToken$.pipe(
          filter((t): t is string => t !== null),
          take(1),
          switchMap(newToken => next(attachToken(req, newToken)))
        );
      }

      isRefreshing = true;
      refreshedToken$.next(null);
      const backend = inject(HttpBackend);
      const direct = new HttpClient(backend);

      return direct.post<AdminTokenResponse>(
        `${environment.apiUrl}/api/admin/auth/refresh`,
        { refresh_token: refreshToken }
      ).pipe(
        switchMap(resp => {
          isRefreshing = false;
          auth.setSession(resp, auth.isRemembered());
          refreshedToken$.next(resp.access_token); // release any queued requests
          return next(attachToken(req, resp.access_token));
        }),
        catchError(refreshErr => {
          isRefreshing = false;
          auth.logout();
          return throwError(() => refreshErr);
        })
      );
    })
  );
};

function attachToken(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  if (!token) return req;
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}
